import { useState, useEffect } from 'react'
import {
  agentsService,
  OptimizerMonitoringResponse,
  AgentStatusSummary,
  ExecutionHistoryItem,
  HandsUpItem,
  AgentExecution
} from '@/services/agents'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  Play,
  AlertTriangle,
  XCircle,
  Bot,
  Activity,
  Hand,
  MoreHorizontal,
  History
} from 'lucide-react'

function formatDuration(ms: number | null): string {
  if (ms === null) return '-'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
}

function formatCost(cost: number | null): string {
  if (cost === null) return '-'
  return `$${cost.toFixed(4)}`
}

function formatTokens(input: number | null, output: number | null): string {
  if (input === null && output === null) return '-'
  return `${(input || 0).toLocaleString()} / ${(output || 0).toLocaleString()}`
}

function formatModel(model: string | null): string {
  if (!model) return '-'
  // Shorten model names like claude-3-haiku-20240307 to claude-3-haiku
  const parts = model.split('-')
  if (parts.length >= 3) {
    return parts.slice(0, 3).join('-')
  }
  return model
}

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

function formatElapsed(startDateStr: string | null): string {
  if (!startDateStr) return '-'
  const start = new Date(startDateStr)
  const now = new Date()
  const diffMs = now.getTime() - start.getTime()

  if (diffMs < 0) return '-'

  const seconds = Math.floor(diffMs / 1000) % 60
  const minutes = Math.floor(diffMs / 60000) % 60
  const hours = Math.floor(diffMs / 3600000)

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  }
  return `${seconds}s`
}

function getStatusBadge(status: string, isRunning: boolean, isStuck: boolean) {
  if (isStuck) {
    return (
      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Stuck
      </Badge>
    )
  }
  if (isRunning) {
    return (
      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
        <Play className="h-3 w-3 mr-1 animate-pulse" />
        Running
      </Badge>
    )
  }
  if (status === 'active') {
    return (
      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        Active
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
      <Clock className="h-3 w-3 mr-1" />
      Inactive
    </Badge>
  )
}

function getExecutionStatusBadge(status: string) {
  switch (status) {
    case 'running':
      return (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          <Play className="h-3 w-3 mr-1 animate-pulse" />
          Running
        </Badge>
      )
    case 'completed':
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Completed
        </Badge>
      )
    case 'failed':
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          <XCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      )
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

function getSeverityBadge(severity: string) {
  switch (severity) {
    case 'critical':
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
          Critical
        </Badge>
      )
    case 'high':
      return (
        <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
          High
        </Badge>
      )
    case 'medium':
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
          Medium
        </Badge>
      )
    default:
      return (
        <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-300">
          Low
        </Badge>
      )
  }
}

export function OptimizerMonitor() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<OptimizerMonitoringResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Executions dialog state
  const [executionsDialogOpen, setExecutionsDialogOpen] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<AgentStatusSummary | null>(null)
  const [agentExecutions, setAgentExecutions] = useState<AgentExecution[]>([])
  const [executionsLoading, setExecutionsLoading] = useState(false)

  const handleViewExecutions = async (agent: AgentStatusSummary) => {
    setSelectedAgent(agent)
    setExecutionsDialogOpen(true)
    setExecutionsLoading(true)
    try {
      const executions = await agentsService.getExecutions(agent.id, undefined, 50)
      setAgentExecutions(executions)
    } catch (err) {
      console.error('Failed to fetch executions:', err)
      setAgentExecutions([])
    } finally {
      setExecutionsLoading(false)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await agentsService.getOptimizerMonitoring()
        setData(response)
      } catch (err: any) {
        console.error('Failed to fetch optimizer monitoring data:', err)
        setError(err.response?.data?.detail || 'Failed to load monitoring data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [])

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-lg font-semibold">Failed to load monitoring data</p>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
        </div>
      </div>
    )
  }

  if (!data) return null

  const { summary, agents, recent_executions, pending_hands_up } = data

  // Identify stuck agents (running > 30 min)
  const now = new Date()
  const stuckThreshold = 30 * 60 * 1000 // 30 minutes
  const stuckAgentIds = new Set(
    agents
      .filter(a => {
        if (!a.current_execution_started_at) return false
        const started = new Date(a.current_execution_started_at)
        return now.getTime() - started.getTime() > stuckThreshold
      })
      .map(a => a.agent_id)
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-semibold">Optimizer Monitor</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time monitoring of agents and their executions
          </p>
        </div>
        <div className="flex items-center gap-2">
          {summary.stuck_agents > 0 && (
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
              <AlertTriangle className="h-3.5 w-3.5 mr-1" />
              {summary.stuck_agents} Stuck
            </Badge>
          )}
          {summary.pending_escalations > 0 && (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              <Hand className="h-3.5 w-3.5 mr-1" />
              {summary.pending_escalations} Escalations
            </Badge>
          )}
          {summary.success_rate_24h !== null && summary.success_rate_24h >= 95 && (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              Healthy
            </Badge>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Agents</CardDescription>
            <CardTitle className="text-2xl">{summary.total_agents}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {summary.active_agents} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Running Now</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              {summary.running_agents}
              {summary.running_agents > 0 && (
                <Play className="h-4 w-4 text-blue-600 animate-pulse" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {summary.stuck_agents > 0 ? (
                <span className="text-orange-600">{summary.stuck_agents} stuck</span>
              ) : 'All healthy'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Executions (24h)</CardDescription>
            <CardTitle className="text-2xl">{summary.executions_24h}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {summary.failed_recently > 0 ? (
                <span className="text-red-600">{summary.failed_recently} with failures</span>
              ) : 'No failures'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Success Rate</CardDescription>
            <CardTitle className="text-2xl">
              {summary.success_rate_24h !== null ? `${summary.success_rate_24h}%` : '-'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Escalations</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              {summary.pending_escalations}
              {summary.pending_escalations > 0 && (
                <Hand className="h-4 w-4 text-yellow-600" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Pending review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Inactive</CardDescription>
            <CardTitle className="text-2xl">{summary.inactive_agents}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Paused agents</p>
          </CardContent>
        </Card>
      </div>

      {/* Agents Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Agent Status
          </CardTitle>
          <CardDescription>Current status of all registered agents</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Elapsed</TableHead>
                <TableHead>Last Completed</TableHead>
                <TableHead>Executions (24h)</TableHead>
                <TableHead>Success/Failed</TableHead>
                <TableHead>Escalations</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                    No agents registered
                  </TableCell>
                </TableRow>
              ) : (
                agents.map((agent) => (
                  <TableRow key={agent.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {agent.color && (
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: agent.color }}
                          />
                        )}
                        <div>
                          <div className="font-medium">{agent.name || agent.agent_id}</div>
                          {agent.name && (
                            <div className="text-xs text-muted-foreground">{agent.agent_id}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{agent.organization_name || '-'}</span>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(
                        agent.status,
                        agent.current_execution_status === 'running',
                        stuckAgentIds.has(agent.agent_id)
                      )}
                    </TableCell>
                    <TableCell>
                      {agent.current_execution_status === 'running' ? (
                        <span className={stuckAgentIds.has(agent.agent_id) ? 'text-orange-600 font-medium' : 'text-blue-600'}>
                          {formatElapsed(agent.current_execution_started_at)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatTimeAgo(agent.last_completed_at)}
                    </TableCell>
                    <TableCell>{agent.total_executions_24h}</TableCell>
                    <TableCell>
                      <span className="text-green-600">{agent.successful_executions_24h}</span>
                      {' / '}
                      <span className={agent.failed_executions_24h > 0 ? 'text-red-600' : ''}>
                        {agent.failed_executions_24h}
                      </span>
                    </TableCell>
                    <TableCell>
                      {agent.pending_hands_up > 0 ? (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          {agent.pending_hands_up}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewExecutions(agent)}>
                            <History className="h-4 w-4 mr-2" />
                            Executions
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Executions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Executions
          </CardTitle>
          <CardDescription>Last 50 agent executions in the past 24 hours</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Cycle</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Tokens</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent_executions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No executions in the last 24 hours
                  </TableCell>
                </TableRow>
              ) : (
                recent_executions.map((exec) => (
                  <TableRow key={exec.id}>
                    <TableCell>
                      <div className="font-medium">{exec.agent_name || exec.agent_id}</div>
                    </TableCell>
                    <TableCell>#{exec.cycle_number}</TableCell>
                    <TableCell>{getExecutionStatusBadge(exec.status)}</TableCell>
                    <TableCell>{formatDuration(exec.duration_ms)}</TableCell>
                    <TableCell>
                      <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                        {formatModel(exec.llm_model)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {formatTokens(exec.tokens_input, exec.tokens_output)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-medium">
                        {formatCost(exec.llm_cost_usd)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {exec.error_message ? (
                        <span className="text-red-600 text-xs truncate max-w-[150px] block" title={exec.error_message}>
                          {exec.error_message.length > 40
                            ? exec.error_message.substring(0, 40) + '...'
                            : exec.error_message}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pending Escalations */}
      {pending_hands_up.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Hand className="h-5 w-5 text-yellow-600" />
              Pending Escalations
            </CardTitle>
            <CardDescription>Agent escalations requiring human review</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending_hands_up.map((hu) => (
                  <TableRow key={hu.id}>
                    <TableCell>
                      <div className="font-medium">{hu.agent_name || hu.agent_id}</div>
                    </TableCell>
                    <TableCell>{getSeverityBadge(hu.severity)}</TableCell>
                    <TableCell>
                      <span className="text-muted-foreground">{hu.category || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm" title={hu.reason || undefined}>
                        {hu.reason
                          ? hu.reason.length > 80
                            ? hu.reason.substring(0, 80) + '...'
                            : hu.reason
                          : '-'}
                      </span>
                    </TableCell>
                    <TableCell>{formatTimeAgo(hu.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Executions Dialog */}
      <Dialog open={executionsDialogOpen} onOpenChange={setExecutionsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Executions: {selectedAgent?.name || selectedAgent?.agent_id}
            </DialogTitle>
            <DialogDescription>
              Execution history for this agent
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {executionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : agentExecutions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No executions found
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cycle</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Tokens</TableHead>
                    <TableHead>Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agentExecutions.map((exec) => (
                    <TableRow key={exec.id}>
                      <TableCell>#{exec.id}</TableCell>
                      <TableCell>{getExecutionStatusBadge(exec.status)}</TableCell>
                      <TableCell>
                        {exec.started_at
                          ? new Date(exec.started_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : '-'}
                      </TableCell>
                      <TableCell>{formatDuration(exec.duration_ms)}</TableCell>
                      <TableCell>
                        <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                          {formatModel(exec.llm_model_used)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {formatTokens(exec.tokens_input, exec.tokens_output)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-medium">
                          {formatCost(exec.llm_cost_usd)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  )
}
